import React, { useEffect, useMemo, useState } from "react";
import { Gift, MinusCircle, PlusCircle, RefreshCw, Search } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

function AdminReferralBonuses() {
  const [data, setData] = useState({ claims: [], bonuses: [], totals: { referral_claimed: 0, manual_credit: 0, manual_debit: 0 } });
  const [query, setQuery] = useState("");
  const [form, setForm] = useState({ user_id: "", amount: "", remark: "", mode: "credit" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const res = await API.get("/admin/referral-bonuses");
      setData(res.data || { claims: [], bonuses: [], totals: { referral_claimed: 0, manual_credit: 0, manual_debit: 0 } });
    } catch {
      setData({ claims: [], bonuses: [], totals: { referral_claimed: 0, manual_credit: 0, manual_debit: 0 } });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredClaims = useMemo(() => {
    const search = query.trim().toLowerCase();
    return (data.claims || []).filter((item) => {
      if (!search) return true;
      return [item.user_name, item.user_phone, item.user_id, item.week_start, item.week_end]
        .filter(Boolean).some((value) => String(value).toLowerCase().includes(search));
    });
  }, [data.claims, query]);

  const filteredBonuses = useMemo(() => {
    const search = query.trim().toLowerCase();
    return (data.bonuses || []).filter((item) => {
      if (!search) return true;
      return [item.user_name, item.user_phone, item.user_id, item.remark, item.ref_type, item.type]
        .filter(Boolean).some((value) => String(value).toLowerCase().includes(search));
    });
  }, [data.bonuses, query]);

  const submitAdjust = async () => {
    if (!form.user_id || !form.amount) { alert("Enter user ID and amount"); return; }
    setLoading(true);
    try {
      await API.post("/admin/referral-bonuses/adjust", {
        user_id: Number(form.user_id),
        amount: Number(form.amount),
        remark: form.remark,
        mode: form.mode,
      });
      setForm({ user_id: "", amount: "", remark: "", mode: "credit" });
      await load();
      alert(`Bonus ${form.mode} done`);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to update bonus");
    } finally {
      setLoading(false);
    }
  };

  const totals = data.totals || {};

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h1 style={title}>Referral Bonus</h1>
            <p style={subtitle}>See claims, weekly bonus payouts and manual credit or debit</p>
          </div>
          <button type="button" style={refreshButton} onClick={load}><RefreshCw size={16} /></button>
        </div>

        <div style={statsGrid}>
          <StatCard label="Referral Claimed" value={formatMoney(totals.referral_claimed)} tone="#eff6ff" color="#2563eb" />
          <StatCard label="Manual Credit" value={formatMoney(totals.manual_credit)} tone="#ecfdf5" color="#16a34a" />
          <StatCard label="Manual Debit" value={formatMoney(totals.manual_debit)} tone="#fef2f2" color="#dc2626" />
        </div>

        <section style={card}>
          <div style={cardHeader}><strong>Bonus Credit / Debit</strong></div>
          <div style={formGrid}>
            <label style={label}>
              <span>User ID</span>
              <input style={input} value={form.user_id} onChange={(e) => setForm((c) => ({ ...c, user_id: e.target.value }))} placeholder="Enter user ID" />
            </label>
            <label style={label}>
              <span>Amount</span>
              <input style={input} value={form.amount} onChange={(e) => setForm((c) => ({ ...c, amount: e.target.value }))} placeholder="Enter amount" />
            </label>
            <label style={label}>
              <span>Mode</span>
              <select style={input} value={form.mode} onChange={(e) => setForm((c) => ({ ...c, mode: e.target.value }))}>
                <option value="credit">Bonus Credit</option>
                <option value="debit">Bonus Debit</option>
              </select>
            </label>
            <label style={{ ...label, gridColumn: "1 / -1" }}>
              <span>Remark</span>
              <input style={input} value={form.remark} onChange={(e) => setForm((c) => ({ ...c, remark: e.target.value }))} placeholder="Reason or note" />
            </label>
          </div>
          <div style={actionRow}>
            <button type="button" style={form.mode === "credit" ? creditButton : debitButton} onClick={submitAdjust} disabled={loading}>
              {form.mode === "credit" ? <PlusCircle size={16} /> : <MinusCircle size={16} />}
              {loading ? "Saving..." : form.mode === "credit" ? "Credit Bonus" : "Debit Bonus"}
            </button>
          </div>
        </section>

        <div style={searchBox}>
          <Search size={16} color="#94a3b8" />
          <input style={searchInput} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by user, phone, ID, remark..." />
        </div>

        <section style={card}>
          <div style={cardHeader}><strong>Referral Claim History</strong><span>{filteredClaims.length}</span></div>
          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>User</th>
                  <th style={th}>Week</th>
                  <th style={th}>Loss</th>
                  <th style={th}>%</th>
                  <th style={th}>Claimed</th>
                  <th style={th}>Wallet</th>
                  <th style={th}>Claimed At</th>
                </tr>
              </thead>
              <tbody>
                {filteredClaims.map((item) => (
                  <tr key={item.id}>
                    <td style={td}>
                      <strong>{item.user_name}</strong>
                      <div style={subText}>{item.user_phone} · ID {item.user_id}</div>
                    </td>
                    <td style={td}>{item.week_start} to {shiftWeekEnd(item.week_end)}</td>
                    <td style={td}>{formatMoney(item.loss_amount)}</td>
                    <td style={td}>{Number(item.commission_percent || 0)}%</td>
                    <td style={{ ...td, color: "#2563eb", fontWeight: 800 }}>{formatMoney(item.commission_amount)}</td>
                    <td style={td}>
                      <div>{formatMoney(item.current_balance)}</div>
                      <div style={subText}>Dep {formatMoney(item.current_deposit)} · Win {formatMoney(item.current_winnings)}</div>
                    </td>
                    <td style={td}>{formatDateTime(item.claimed_at || item.created_at)}</td>
                  </tr>
                ))}
                {!filteredClaims.length && <tr><td style={emptyCell} colSpan={7}>No referral claims yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        <section style={card}>
          <div style={cardHeader}><strong>Bonus Ledger</strong><span>{filteredBonuses.length}</span></div>
          <div style={tableWrap}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>User</th>
                  <th style={th}>Type</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Remark</th>
                  <th style={th}>Balance</th>
                  <th style={th}>Approved By</th>
                  <th style={th}>Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredBonuses.map((item) => (
                  <tr key={`${item.id}-${item.ref_type}`}>
                    <td style={td}>
                      <strong>{item.user_name}</strong>
                      <div style={subText}>{item.user_phone} · ID {item.user_id}</div>
                    </td>
                    <td style={td}><span style={typePill(item)}>{item.type || item.ref_type || "-"}</span></td>
                    <td style={{ ...td, color: getAmountColor(item), fontWeight: 800 }}>{formatSignedAmount(item)}</td>
                    <td style={td}>{item.remark || "-"}</td>
                    <td style={td}>{formatMoney(item.wallet_balance)}</td>
                    <td style={td}>{item.approved_by_name || "-"}</td>
                    <td style={td}>{formatDateTime(item.updated_at || item.created_at)}</td>
                  </tr>
                ))}
                {!filteredBonuses.length && <tr><td style={emptyCell} colSpan={7}>No bonus entries found.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </section>
    </AdminLayout>
  );
}

function typePill(item) {
  const ref = String(item.ref_type || "").toLowerCase();
  if (ref === "manual_bonus_debit") return { ...pillBase, background: "#fee2e2", color: "#b91c1c" };
  if (ref === "manual_bonus_credit") return { ...pillBase, background: "#dcfce7", color: "#166534" };
  return { ...pillBase, background: "#dbeafe", color: "#1d4ed8" };
}

function getAmountColor(item) {
  const ref = String(item.ref_type || "").toLowerCase();
  return ref === "manual_bonus_debit" ? "#dc2626" : "#16a34a";
}

function formatSignedAmount(item) {
  const amount = Number(item.amount || 0);
  const ref = String(item.ref_type || "").toLowerCase();
  return `${ref === "manual_bonus_debit" ? "-" : "+"}${formatMoney(amount)}`;
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function shiftWeekEnd(value) {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function StatCard({ label, value, tone, color }) {
  return (
    <article style={{ ...statCard, background: tone }}>
      <Gift size={18} color={color} />
      <strong style={{ ...statValue, color }}>{value}</strong>
      <span style={statLabel}>{label}</span>
    </article>
  );
}

const page = { display: "grid", gap: 18 };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 };
const title = { margin: 0, fontSize: 20, fontWeight: 900 };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: 13 };
const refreshButton = { width: 44, height: 44, borderRadius: 14, border: "1px solid #dbe3f0", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" };
const statsGrid = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14 };
const statCard = { borderRadius: 20, padding: 18, border: "1px solid #e5e7eb", display: "grid", gap: 8 };
const statValue = { fontSize: 22, fontWeight: 900 };
const statLabel = { fontSize: 13, color: "#475569" };
const card = { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 20, overflow: "hidden" };
const cardHeader = { padding: 18, borderBottom: "1px solid #eef2f7", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 };
const formGrid = { padding: 18, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14 };
const label = { display: "grid", gap: 8, fontSize: 12, color: "#475569", fontWeight: 700 };
const input = { height: 48, border: "1px solid #cbd5e1", borderRadius: 14, padding: "0 14px", fontSize: 15 };
const actionRow = { padding: "0 18px 18px" };
const creditButton = { minHeight: 48, padding: "0 18px", borderRadius: 14, border: 0, background: "#16a34a", color: "#fff", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" };
const debitButton = { minHeight: 48, padding: "0 18px", borderRadius: 14, border: 0, background: "#dc2626", color: "#fff", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" };
const searchBox = { display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #dbe3f0", borderRadius: 16, padding: "0 14px", height: 50 };
const searchInput = { width: "100%", border: 0, outline: 0, fontSize: 15 };
const tableWrap = { overflowX: "auto" };
const table = { width: "100%", borderCollapse: "collapse" };
const th = { textAlign: "left", padding: "14px 16px", color: "#64748b", fontSize: 12, borderBottom: "1px solid #eef2f7", whiteSpace: "nowrap" };
const td = { padding: "14px 16px", color: "#0f172a", fontSize: 14, borderBottom: "1px solid #f8fafc", verticalAlign: "top" };
const subText = { marginTop: 4, color: "#64748b", fontSize: 12 };
const emptyCell = { padding: 28, textAlign: "center", color: "#94a3b8" };
const pillBase = { display: "inline-flex", alignItems: "center", minHeight: 28, padding: "0 10px", borderRadius: 999, fontWeight: 800, fontSize: 12 };

export default AdminReferralBonuses;
